import { useEffect, useState } from "react";
import { Copy, Check, Users, Send, Loader2, RotateCw, CalendarPlus, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/format";

const InviteForm = ({ tenantId, initialPwd, onDone, onClose }) => {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState(initialPwd || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tenants/${tenantId}/demo-invite`, { email, password: pwd, app_url: window.location.origin });
      toast.success(`Link e password demo inviati a ${data.email}`);
      onDone(pwd); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const noEnter = (e) => { if (e.key === "Enter") e.preventDefault(); };
  const inp = "w-full h-9 rounded border border-slate-700 bg-slate-900/60 px-2 text-sm text-slate-100 focus:outline-none focus:border-amber-500";
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="demo-invite-form">
      <div><label className="text-xs text-slate-400">Email dell'utente che richiede la demo</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={noEnter} data-testid="demo-invite-email" className={inp} /></div>
      <div><label className="text-xs text-slate-400">Password demo (vale per tutti gli utenti demo dello studio)</label>
        <input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} onKeyDown={noEnter} data-testid="demo-invite-password" className={`${inp} font-mono`} /></div>
      <div className="flex gap-2">
        <button type="button" onClick={send} disabled={busy || !email || !pwd} data-testid="demo-invite-send"
          className="flex items-center gap-1.5 rounded bg-amber-500 hover:bg-amber-600 px-3 py-1.5 text-xs font-semibold text-[#0a0d14] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Invia
        </button>
        <button type="button" onClick={onClose} data-testid="demo-invite-cancel" className="rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 transition-colors">Annulla</button>
      </div>
    </div>
  );
};

const ResendButton = ({ tenantId, u, onDone }) => {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/demo-users/${u.id}/resend`, { app_url: window.location.origin });
      toast.success(`Link e password demo reinviati a ${u.email}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" disabled={busy} data-testid={`demo-user-resend-${u.id}`}
          className="flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}Reinvia
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="demo-resend-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Reinviare le credenziali demo?</AlertDialogTitle>
          <AlertDialogDescription>Verrà inviata a <strong>{u.email}</strong> una email con il link di accesso e la password demo attuale dello studio.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="demo-resend-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction onClick={resend} data-testid="demo-resend-confirm">Invia</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const ExtendButton = ({ tenantId, u, onDone }) => {
  const [open, setOpen] = useState(false);
  const [giorni, setGiorni] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tenants/${tenantId}/demo-users/${u.id}/extend`, { giorni: parseInt(giorni, 10) });
      toast.success(`Scadenza di ${u.email} estesa al ${formatDate(data.scadenza)}`);
      setOpen(false); setGiorni(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid={`demo-user-extend-${u.id}`}
      className="flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800 transition-colors">
      <CalendarPlus className="h-3 w-3" />Estendi
    </button>
  );
  return (
    <div className="flex items-center gap-1" data-testid={`demo-user-extend-form-${u.id}`}>
      <input type="number" min={1} step={1} value={giorni} onChange={(e) => setGiorni(e.target.value)} placeholder="Giorni" autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (giorni > 0) save(); } }}
        data-testid={`demo-user-extend-days-${u.id}`}
        className="w-16 h-6 rounded border border-slate-700 bg-slate-900/60 px-1.5 text-xs text-slate-100 focus:outline-none focus:border-amber-500" />
      <button type="button" onClick={save} disabled={busy || !(parseInt(giorni, 10) > 0)} data-testid={`demo-user-extend-save-${u.id}`}
        className="rounded bg-amber-500 hover:bg-amber-600 px-2 py-0.5 text-xs font-semibold text-[#0a0d14] disabled:opacity-60">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "OK"}
      </button>
      <button type="button" onClick={() => { setOpen(false); setGiorni(""); }} data-testid={`demo-user-extend-cancel-${u.id}`}
        className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:bg-slate-800">Annulla</button>
    </div>
  );
};

const DeleteButton = ({ tenantId, u, onDone }) => {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/tenants/${tenantId}/demo-users/${u.id}`);
      toast.success(`Utente demo ${u.email} eliminato con tutti i suoi dati`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" disabled={busy} data-testid={`demo-user-delete-${u.id}`} title="Elimina utente demo"
          className="flex items-center gap-1 rounded border border-red-500/50 px-2 py-0.5 text-xs text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}Elimina
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="demo-delete-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminare definitivamente l'utente demo?</AlertDialogTitle>
          <AlertDialogDescription>Verranno cancellati l'account <strong>{u.email}</strong> e tutti i dati che ha inserito (pratiche, anagrafiche, allegati…). L'operazione non può essere annullata.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="demo-delete-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction onClick={del} data-testid="demo-delete-confirm" className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const DemoUsersPanel = ({ tenantId, codice, password }) => {
  const [users, setUsers] = useState([]);
  const [copied, setCopied] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pwd, setPwd] = useState(password || "demo123");
  const link = codice ? `${window.location.origin}/demo/${codice}` : "";
  const loadUsers = () => api.get(`/admin/tenants/${tenantId}/demo-users`).then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => { loadUsers(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = link; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-3" data-testid="demo-users-panel">
      {link && (
        <div>
          <div className="text-xs text-slate-400 mb-1">Link di registrazione utenti demo (password fissa: <span className="font-mono text-slate-200" data-testid="demo-current-password">{pwd}</span>)</div>
          <div className="flex gap-2">
            <code className="flex-1 truncate rounded border border-slate-700 bg-slate-900/60 px-2 py-1.5 text-xs text-slate-200" data-testid="demo-register-link">{link}</code>
            <button type="button" onClick={copy} data-testid="demo-copy-link-button"
              className="flex items-center gap-1 rounded border border-slate-700 px-2 text-xs text-slate-200 hover:bg-slate-800 transition-colors">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copiato!" : "Copia"}
            </button>
          </div>
        </div>
      )}
      {inviteOpen ? (
        <InviteForm tenantId={tenantId} initialPwd={pwd} onDone={(p) => { setPwd(p); loadUsers(); }} onClose={() => setInviteOpen(false)} />
      ) : (
        <button type="button" onClick={() => setInviteOpen(true)} data-testid="demo-invite-button"
          className="flex items-center gap-1.5 rounded bg-amber-500 hover:bg-amber-600 px-3 py-1.5 text-xs font-semibold text-[#0a0d14] transition-colors">
          <Send className="h-3.5 w-3.5" />Invia link e password demo
        </button>
      )}
      <div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1"><Users className="h-3.5 w-3.5" />Utenti demo registrati ({users.length})</div>
        {users.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="demo-users-empty">Nessun utente demo registrato.</p>
        ) : (
          <table className="w-full text-xs" data-testid="demo-users-table">
            <thead><tr className="text-slate-500 text-left"><th className="py-1">Email</th><th>Nome</th><th>Registrato il</th><th>Scadenza</th><th>Dati cancellati il</th><th>Ultimo invio</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-800">
              {users.map((u) => (
                <tr key={u.id} className="text-slate-300" data-testid={`demo-user-row-${u.id}`}>
                  <td className="py-1">{u.email}</td><td>{u.name}</td>
                  <td>{formatDate(u.registered_at)}</td>
                  <td data-testid={`demo-user-scadenza-${u.id}`}>{u.scadenza ? formatDate(u.scadenza) : "—"}</td>
                  <td>{u.cancellazione_il ? formatDate(u.cancellazione_il) : "—"}</td>
                  <td data-testid={`demo-user-last-sent-${u.id}`}>{u.last_sent_at ? formatDateTime(u.last_sent_at) : "—"}</td>
                  <td className="text-right"><div className="flex justify-end gap-1">
                    <ExtendButton tenantId={tenantId} u={u} onDone={loadUsers} />
                    <ResendButton tenantId={tenantId} u={u} onDone={loadUsers} />
                    <DeleteButton tenantId={tenantId} u={u} onDone={loadUsers} />
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
